// Copyright © 2023 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import Editor from "@monaco-editor/react";
import PropTypes from "prop-types";

import { Button, Loader } from "components/elements";
import { Switch } from "components/form/elements";
import { trans } from "src/translations";

import styles from "./CodeEditor.scss";
import { THEMES, useEditorTheme } from "./hooks/useEditorTheme";

const EDITOR_LINE_HEIGHT = 18;

/**
 * @typedef {Object} CodeEditorProps
 * @property {string} [download] - Filename for download functionality
 * @property {boolean} [enableMouseWheel=false] - Whether to enable mouse wheel scrolling in the editor
 * @property {string} [height="500px"] - Height of the code editor
 * @property {string} value - The code content to display in the editor
 * @property {string} [dataTestId="code"] - Test ID for testing purposes
 * @property {string} [editorTheme] - Theme for the editor (light or dark)
 * @property {Function} [toggleTheme] - Function to toggle between light and dark themes
 */

/**
 * CodeEditor component that provides a Monaco-based code editor with theme switching and download functionality
 * @param {CodeEditorProps} props - The component props
 * @returns {JSX.Element} The rendered code editor component
 */
export const CodeEditor = ({
  download,
  enableMouseWheel = false,
  height = "500px",
  value,
  dataTestId = "code",
  ...editorThemeProps
}) => {
  const defaultTheme = useEditorTheme();
  const {
    editorTheme = defaultTheme.editorTheme,
    toggleTheme = defaultTheme.toggleTheme,
  } = editorThemeProps;
  const isLightTheme = editorTheme === THEMES.light;

  const renderButtons = () => (
    <div className={styles.buttons}>
      {download ? (
        <Button
          to={`data:text/json;base64,${btoa(value)}`}
          download={download}
          dataTestId={`${dataTestId}-download-button`}
        >
          {trans.DOWNLOAD()}
        </Button>
      ) : null}
      <div className={styles.themeSwitch}>
        <p className={styles.modeName}>
          {isLightTheme ? trans.LIGHT_MODE() : trans.DARK_MODE()}
        </p>
        <Switch
          className={styles.switchButton}
          checked={isLightTheme}
          onChange={toggleTheme}
        />
      </div>
    </div>
  );

  return (
    <div>
      {renderButtons()}
      <Editor
        className={styles.editor}
        height={height}
        language="json"
        loading={<Loader />}
        options={{
          automaticLayout: true,
          lineHeight: EDITOR_LINE_HEIGHT,
          minimap: { enabled: false },
          padding: { top: EDITOR_LINE_HEIGHT, bottom: EDITOR_LINE_HEIGHT },
          readOnly: true,
          scrollbar: { handleMouseWheel: enableMouseWheel },
          scrollBeyondLastLine: false,
          tabSize: 2,
        }}
        theme={editorTheme}
        value={value}
      />
    </div>
  );
};

CodeEditor.propTypes = {
  download: PropTypes.string,
  enableMouseWheel: PropTypes.bool,
  height: PropTypes.string,
  value: PropTypes.string.isRequired,
  dataTestId: PropTypes.string,
  editorTheme: PropTypes.oneOf([THEMES.light, THEMES.dark]),
  toggleTheme: PropTypes.func,
};
